import { router } from '@inertiajs/vue3';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

declare const route: (name: string, params?: unknown) => string;

type Paginator<T> = {
    data: T[];
    meta?: { total?: number } & Record<string, unknown>;
} & Record<string, unknown>;

type UserRow = {
    id: number | string;
};

export const useUsersStore = defineStore('users', () => {
    const paginator = ref<Paginator<UserRow> | null>(null);

    // Internally track processing as a Set of "<action>-<id>" keys for easy lookups
    const processingKeys = ref<Set<string>>(new Set());
    const processingItems = computed<string[]>(() => Array.from(processingKeys.value));

    const buildKey = (action: string, userId: number | string): string => `${action}-${userId}`;

    const addProcessing = (action: string, userId: number | string): void => {
        processingKeys.value.add(buildKey(action, userId));
        // recreate Set to ensure reactivity for some edge renderers
        processingKeys.value = new Set(processingKeys.value);
    };

    const removeProcessing = (action: string, userId: number | string): void => {
        processingKeys.value.delete(buildKey(action, userId));
        processingKeys.value = new Set(processingKeys.value);
    };

    const isProcessing = (userId: number | string, action?: string): boolean => {
        if (action) {
            return processingKeys.value.has(buildKey(action, userId));
        }
        const suffix = `-${userId}`;
        return Array.from(processingKeys.value).some((key) => key.endsWith(suffix));
    };

    const setPaginatorFromUsersProp = (val: unknown): void => {
        // Expecting val to be a paginator-like object with a data array
        const incoming = val as Paginator<UserRow> | null | undefined;
        if (!incoming || !Array.isArray(incoming.data)) {
            return;
        }
        // Always refresh the paginator so row mutations from the server are reflected in the table
        paginator.value = { ...incoming, data: [...incoming.data] };
    };

    const showDetails = (user: UserRow): void => {
        if (isProcessing(user.id, 'delete')) return;
        router.visit(route('users.show', user.id), { preserveState: true });
    };

    const editUser = (user: UserRow): void => {
        router.visit(route('users.edit', user.id), { preserveState: true });
    };

    const revokeAccess = (user: UserRow): void => {
        addProcessing('revoke', user.id);
        router.post(
            route('users.revoke_access', user.id),
            {},
            {
                preserveState: true,
                preserveScroll: true,
                only: ['users', 'errors', 'flash'],
                onFinish: () => {
                    removeProcessing('revoke', user.id);
                },
            },
        );
    };

    const restoreAccess = (user: UserRow): void => {
        addProcessing('restore', user.id);
        router.get(
            route('users.restore_access', user.id),
            {},
            {
                preserveState: true,
                preserveScroll: true,
                only: ['users', 'errors', 'flash'],
                onFinish: () => {
                    removeProcessing('restore', user.id);
                },
            },
        );
    };

    const inviteUser = (user: UserRow): void => {
        addProcessing('invite', user.id);
        router.post(
            route('users.invite', user.id),
            {},
            {
                preserveState: true,
                preserveScroll: true,
                only: ['users', 'errors', 'flash'],
                onFinish: () => {
                    removeProcessing('invite', user.id);
                },
            },
        );
    };

    const deleteUser = (user: UserRow): void => {
        if (isProcessing(user.id, 'delete')) return;
        addProcessing('delete', user.id);

        const previous = paginator.value ? { ...paginator.value, data: [...paginator.value.data] } : null;
        router.delete(route('users.destroy', user.id), {
            preserveState: true,
            preserveScroll: true,
            // Do not reload the users prop; we will optimistically update the local items list.
            only: ['flash'],
            onSuccess: () => {
                if (paginator.value && Array.isArray(paginator.value.data)) {
                    paginator.value = {
                        ...paginator.value,
                        data: paginator.value.data.filter((u: UserRow) => u.id !== user.id),
                        meta: paginator.value.meta
                            ? { ...paginator.value.meta, total: Math.max(0, (paginator.value.meta.total || 0) - 1) }
                            : paginator.value.meta,
                    };
                }
            },
            onError: () => {
                if (previous) {
                    paginator.value = previous;
                }
            },
            onFinish: () => {
                removeProcessing('delete', user.id);
            },
        });
    };

    const impersonate = (user: UserRow): void => {
        router.get(route('users.impersonate', user.id), {}, { preserveState: true });
    };

    return {
        // state
        paginator,
        processingItems,
        // helpers
        isProcessing,
        setPaginatorFromUsersProp,
        // operations
        showDetails,
        editUser,
        revokeAccess,
        restoreAccess,
        inviteUser,
        deleteUser,
        impersonate,
    };
});
